import React, { useEffect, useMemo, useState } from 'react';
import { FiPlus, FiTrash2, FiEdit2 } from 'react-icons/fi';
import { Timestamp, orderBy, limit } from 'firebase/firestore';
import {
  Card,
  Table,
  Badge,
  Button,
  Input,
  Modal,
  Pagination,
  CsvExportButton,
} from '../../components/UI';
import { useFirestoreCollection, usePagination } from '../../hooks';
import { writeAdminConfig, deleteAdminConfig } from '../../services/adminService';
import styles from './DiscountCodes.module.css';

// Schema: discountCodes.{ id (used as the code, uppercased), discountType:
//   'percentage'|'fixed', value, expiresAt, maxUses, usedCount, minOrderValue?,
//   oncePerUser?, isActive, description, createdAt, lastRedeemedAt }
//
// Redemption is server-side. `validateDiscountCode` previews a code for the
// checkout screen and `createShopOrderPaymentIntent` re-derives the money off
// from the cart subtotal, refusing the payment if the client disagrees.
// `usedCount` is incremented once per PaymentIntent by the Stripe webhook, so
// abandoned checkouts never burn a use — see redeemDiscountForPaymentIntent.
//
// Codes apply to Stripe-billed SHOP ORDERS only. Subscription plans are in-app
// purchases owned by Apple and Google; use App Store Offer Codes / Play promo
// codes for those, configured in the store consoles.

const formatDate = (value) => {
  if (!value) return '-';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
};

const blankDraft = {
  code: '',
  description: '',
  discountType: 'percentage',
  value: 10,
  maxUses: 100,
  minOrderValue: 0,
  oncePerUser: false,
  expiresInDays: 30,
  isActive: true,
};

const DiscountCodes = () => {
  const [showEdit, setShowEdit] = useState(false);
  const [draft, setDraft] = useState(blankDraft);
  const [editingId, setEditingId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const constraints = useMemo(() => [orderBy('createdAt', 'desc'), limit(500)], []);
  const { data: codes, loading } = useFirestoreCollection('discountCodes', constraints);

  const { page, pageSize, pageCount, pageData, totalItems, setPage, setPageSize } =
    usePagination(codes, 25);

  useEffect(() => {}, []);

  const openCreate = () => {
    setDraft(blankDraft);
    setEditingId(null);
    setShowEdit(true);
  };

  const openEdit = (code) => {
    const expiresInDays = code.expiresAt?.toDate
      ? Math.max(1, Math.round((code.expiresAt.toDate() - Date.now()) / 86400000))
      : 30;
    setDraft({
      code: code.id,
      description: code.description || '',
      discountType: code.discountType || 'percentage',
      value: code.value ?? 10,
      maxUses: code.maxUses ?? 100,
      minOrderValue: code.minOrderValue ?? 0,
      oncePerUser: code.oncePerUser === true,
      expiresInDays,
      isActive: code.isActive !== false,
    });
    setEditingId(code.id);
    setShowEdit(true);
  };

  const handleSave = async () => {
    if (!draft.code) {
      setErrorMsg('Code is required');
      return;
    }
    // Must match normalizeDiscountCode() in functions/index.js, or the server
    // will reject every attempt to use the code that is created here.
    const candidate = (editingId || draft.code.trim().toUpperCase());
    if (!/^[A-Z0-9_-]{1,32}$/.test(candidate)) {
      setErrorMsg('Code must be 1-32 characters: letters, numbers, hyphen or underscore only.');
      return;
    }
    if (draft.discountType === 'percentage' && (Number(draft.value) <= 0 || Number(draft.value) > 100)) {
      setErrorMsg('A percentage discount must be between 1 and 100.');
      return;
    }
    if (Number(draft.value) <= 0) {
      setErrorMsg('Discount value must be greater than zero.');
      return;
    }
    setActionLoading(true);
    setErrorMsg('');
    try {
      const id = editingId || draft.code.trim().toUpperCase();
      const expiresAt = Timestamp.fromDate(
        new Date(Date.now() + Number(draft.expiresInDays) * 86400000)
      );
      await writeAdminConfig({
        collection: 'discountCodes',
        docId: id,
        updates: {
          discountType: draft.discountType,
          value: Number(draft.value),
          maxUses: Number(draft.maxUses),
          minOrderValue: Number(draft.minOrderValue) || 0,
          oncePerUser: !!draft.oncePerUser,
          isActive: !!draft.isActive,
          description: draft.description || '',
          expiresAt,
          // Only set initial usedCount on first create — CF stamps timestamps.
          // Never reset it on edit: that would hand back uses already redeemed.
          ...(editingId ? {} : { usedCount: 0 }),
        },
      });
      setShowEdit(false);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Save failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete discount code "${code.id}"?`)) return;
    setActionLoading(true);
    try {
      await deleteAdminConfig({ collection: 'discountCodes', docId: code.id });
    } catch (err) {
      console.error(err);
      setErrorMsg(`Delete failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const csvColumns = [
    { key: 'id', label: 'Code' },
    { key: 'discountType', label: 'Type' },
    { key: 'value', label: 'Value' },
    { key: 'maxUses', label: 'Max Uses' },
    { key: 'usedCount', label: 'Used' },
    { key: 'minOrderValue', label: 'Min Order' },
    { key: 'oncePerUser', label: 'Once Per Customer', accessor: (c) => (c.oncePerUser ? 'yes' : 'no') },
    { key: 'lastRedeemedAt', label: 'Last Redeemed', accessor: (c) => formatDate(c.lastRedeemedAt) },
    { key: 'isActive', label: 'Active', accessor: (c) => (c.isActive ? 'yes' : 'no') },
    { key: 'expiresAt', label: 'Expires', accessor: (c) => formatDate(c.expiresAt) },
    { key: 'description', label: 'Description' },
  ];

  const columns = [
    {
      key: 'id',
      title: 'Code',
      render: (id) => <code className={styles.code}>{id}</code>,
    },
    {
      key: 'discountType',
      title: 'Type',
      render: (v) => <Badge variant="secondary">{v || 'percentage'}</Badge>,
    },
    {
      key: 'value',
      title: 'Value',
      render: (v, row) =>
        row.discountType === 'percentage' ? `${v ?? 0}%` : `€${v ?? 0}`,
    },
    {
      key: 'usage',
      title: 'Usage',
      render: (_, row) => (
        <div>
          <div>{`${row.usedCount ?? 0} / ${row.maxUses || '∞'}`}</div>
          {(row.minOrderValue > 0 || row.oncePerUser) && (
            <div className={styles.usageMeta}>
              {[
                row.minOrderValue > 0 ? `min €${row.minOrderValue}` : null,
                row.oncePerUser ? '1 per customer' : null,
              ].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      ),
    },
    { key: 'expiresAt', title: 'Expires', render: (v) => formatDate(v) },
    {
      key: 'isActive',
      title: 'Status',
      render: (v) => (v === false ? <Badge variant="inactive">disabled</Badge> : <Badge variant="success">active</Badge>),
    },
    {
      key: 'actions',
      title: '',
      width: '120px',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button variant="ghost" size="sm" icon={<FiEdit2 />} onClick={() => openEdit(row)} />
          <Button variant="danger" size="sm" icon={<FiTrash2 />} onClick={() => handleDelete(row)} />
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Discount Codes</h1>
          {!loading && (
            <Badge variant="primary">
              {codes.filter((c) => {
                if (c.isActive === false) return false;
                const ms = c.expiresAt?.toMillis
                  ? c.expiresAt.toMillis()
                  : c.expiresAt ? new Date(c.expiresAt).getTime() : null;
                if (ms && ms < Date.now()) return false;
                return !(c.maxUses > 0 && (c.usedCount ?? 0) >= c.maxUses);
              }).length} redeemable
            </Badge>
          )}
        </div>
        <p className={styles.subtitle}>
          Create and manage promotional discount codes
          <span className={styles.liveText}> Live</span>
        </p>
      </div>

      {errorMsg && (
        <Card>
          <div style={{ color: '#f87171' }}>{errorMsg}</div>
        </Card>
      )}

      <Card>
        <div className={styles.toolbar}>
          <Button variant="primary" icon={<FiPlus />} onClick={openCreate}>
            New Code
          </Button>
          <CsvExportButton rows={codes} columns={csvColumns} filename="dierend-discount-codes" />
        </div>

        <Table columns={columns} data={pageData} loading={loading} emptyMessage="No discount codes" />

        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Modal
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        title={editingId ? `Edit ${editingId}` : 'New Discount Code'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowEdit(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} loading={actionLoading}>
              Save
            </Button>
          </>
        }
      >
        <div className={styles.form}>
          <Input
            label="Code"
            value={draft.code}
            onChange={(e) => setDraft({ ...draft, code: e.target.value })}
            disabled={!!editingId}
            placeholder="WELCOME10"
          />
          <Input
            label="Description"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            placeholder="Welcome offer for new users"
          />

          <div className={styles.row}>
            <div>
              <label className={styles.label}>Type</label>
              <select
                className={styles.select}
                value={draft.discountType}
                onChange={(e) => setDraft({ ...draft, discountType: e.target.value })}
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed (€)</option>
              </select>
            </div>
            <Input
              label={draft.discountType === 'percentage' ? 'Percent off' : 'Amount off (€)'}
              type="number"
              value={draft.value}
              onChange={(e) => setDraft({ ...draft, value: e.target.value })}
            />
          </div>

          <div className={styles.row}>
            <Input
              label="Max uses"
              type="number"
              value={draft.maxUses}
              onChange={(e) => setDraft({ ...draft, maxUses: e.target.value })}
            />
            <Input
              label="Expires in (days)"
              type="number"
              value={draft.expiresInDays}
              onChange={(e) => setDraft({ ...draft, expiresInDays: e.target.value })}
            />
          </div>

          <Input
            label="Minimum order (€, 0 = no minimum)"
            type="number"
            value={draft.minOrderValue}
            onChange={(e) => setDraft({ ...draft, minOrderValue: e.target.value })}
          />

          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={draft.oncePerUser}
              onChange={(e) => setDraft({ ...draft, oncePerUser: e.target.checked })}
            />
            <span>One use per customer</span>
          </label>

          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={draft.isActive}
              onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
            />
            <span>Active</span>
          </label>

          <p className={styles.hint}>
            Applies to shop orders paid through Stripe. Subscription plans are
            in-app purchases owned by Apple and Google — use App Store Offer Codes
            or Play promo codes for those, set up in the store consoles.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default DiscountCodes;
